import { useState } from 'react';
import {
  Block,
  IntervalSet,
  Ramp,
  Workout,
  WorkoutSection,
} from '../types/workout';
import { TimeInput } from './TimeInput';

export function WorkoutBuilder({
  onStart,
  isConnectedToTrainer,
}: {
  onStart: (_workout: Workout) => void;
  isConnectedToTrainer: boolean;
}) {
  const [sections, setSections] = useState<WorkoutSection[]>([]);

  const updateSection = (index: number, value: WorkoutSection) => {
    const newSections = [...sections];
    newSections[index] = value;
    setSections(newSections);
  };

  const addBlock = () => {
    const newBlock: Block = {
      id: Date.now().toString(),
      type: 'block',
      name: `Block ${sections.filter((s) => s.type === 'block').length + 1}`,
      duration: 0,
      targetPower: 0,
      durationValid: false,
      powerValid: false,
    };
    setSections([...sections, newBlock]);
  };

  const addIntervalSet = () => {
    const newIntervalSet: IntervalSet = {
      id: Date.now().toString(),
      type: 'interval',
      name: `Intervals ${sections.filter((s) => s.type === 'interval').length + 1}`,
      onInterval: {
        duration: 0,
        targetPower: 0,
        durationValid: false,
        powerValid: false,
      },
      restInterval: {
        duration: 0,
        targetPower: 0,
        durationValid: false,
        powerValid: false,
      },
      repeats: 1,
      repeatsValid: true,
    };
    setSections([...sections, newIntervalSet]);
  };

  const addRamp = () => {
    const newRamp: Ramp = {
      id: Date.now().toString(),
      type: 'ramp',
      name: `Ramp ${sections.filter((s) => s.type === 'ramp').length + 1}`,
      duration: 0,
      startPower: 0,
      endPower: 0,
      durationValid: false,
      startPowerValid: false,
      endPowerValid: false,
    };
    setSections([...sections, newRamp]);
  };

  const removeSection = (index: number) => {
    setSections(sections.filter((_, i) => i !== index));
  };

  const moveSectionUp = (index: number) => {
    if (index > 0) {
      const newSections = [...sections];
      [newSections[index - 1], newSections[index]] = [
        newSections[index],
        newSections[index - 1],
      ];
      setSections(newSections);
    }
  };

  const moveSectionDown = (index: number) => {
    if (index < sections.length - 1) {
      const newSections = [...sections];
      [newSections[index], newSections[index + 1]] = [
        newSections[index + 1],
        newSections[index],
      ];
      setSections(newSections);
    }
  };

  const isFormValid =
    sections.length > 0 &&
    sections.every((section) => {
      if (section.type === 'block') {
        return section.durationValid && section.powerValid;
      } else if (section.type === 'interval') {
        return (
          section.onInterval.durationValid &&
          section.onInterval.powerValid &&
          section.restInterval.durationValid &&
          section.restInterval.powerValid &&
          section.repeatsValid &&
          section.repeats > 0
        );
      } else if (section.type === 'ramp') {
        return (
          section.durationValid &&
          section.startPowerValid &&
          section.endPowerValid
        );
      }
      return false;
    });

  const readyToStart = isFormValid && isConnectedToTrainer;

  const handleStart = () => {
    if (!isFormValid) return;
    onStart({ sections });
  };

  // Calculate total workout time for preview
  const totalTime = () => {
    return sections.reduce((sum, section) => {
      if (section.type === 'block') {
        return sum + section.duration;
      } else if (section.type === 'interval') {
        return (
          sum +
          (section.onInterval.duration + section.restInterval.duration) *
            section.repeats
        );
      } else if (section.type === 'ramp') {
        return sum + section.duration;
      }
      return sum;
    }, 0);
  };

  const formatTime = (seconds: number) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex space-x-4">
        <button
          onClick={addBlock}
          className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
        >
          Add Block
        </button>
        <button
          onClick={addIntervalSet}
          className="bg-purple-500 text-white p-2 rounded hover:bg-purple-600"
        >
          Add Interval Set
        </button>
        <button
          onClick={addRamp}
          className="bg-green-500 text-white p-2 rounded hover:bg-green-600"
        >
          Add Ramp
        </button>
      </div>

      {sections.length === 0 && (
        <div className="text-gray-500 text-center p-8 border-2 border-dashed border-gray-300 rounded">
          Add a block or interval set to start building your workout
        </div>
      )}

      {/* Workout Sections */}
      <div className="space-y-4">
        {sections.map((section, index) => (
          <div
            key={section.id}
            className="border-2 border-gray-300 rounded p-4 bg-gray-50"
          >
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center space-x-2">
                <span className="text-sm bg-gray-200 px-2 py-1 rounded">
                  {section.type === 'block'
                    ? 'Block'
                    : section.type === 'interval'
                      ? 'Intervals'
                      : 'Ramp'}
                </span>
                <input
                  type="text"
                  value={section.name}
                  onChange={(e) =>
                    updateSection(index, { ...section, name: e.target.value })
                  }
                  className="font-medium text-lg border-b border-transparent hover:border-gray-300 focus:border-blue-500 bg-transparent outline-none"
                />
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => moveSectionUp(index)}
                  disabled={index === 0}
                  className="px-2 py-1 bg-gray-400 text-white rounded text-sm disabled:bg-gray-300"
                >
                  ↑
                </button>
                <button
                  onClick={() => moveSectionDown(index)}
                  disabled={index === sections.length - 1}
                  className="px-2 py-1 bg-gray-400 text-white rounded text-sm disabled:bg-gray-300"
                >
                  ↓
                </button>
                <button
                  onClick={() => removeSection(index)}
                  className="px-2 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                >
                  Remove
                </button>
              </div>
            </div>

            {section.type === 'block' ? (
              // Single Block
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium">Time</label>
                  <TimeInput
                    value={(section as Block).duration}
                    onChange={(duration) =>
                      updateSection(index, { ...section, duration })
                    }
                    onValidityChange={(isValid) =>
                      updateSection(index, {
                        ...section,
                        durationValid: isValid,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">Watts</label>
                  <input
                    type="number"
                    value={(section as Block).targetPower.toString()}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      updateSection(index, {
                        ...section,
                        targetPower: Number(val),
                        powerValid: !!val,
                      });
                    }}
                    className={`border p-2 w-full ${(section as Block).powerValid ? '' : 'border-red-500'}`}
                  />
                </div>
              </div>
            ) : section.type === 'interval' ? (
              // Interval Set
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* On Interval */}
                <div className="space-y-2">
                  <h4 className="font-medium text-green-700">Work Interval</h4>
                  <div>
                    <label className="block text-sm font-medium">Time</label>
                    <TimeInput
                      value={(section as IntervalSet).onInterval.duration}
                      onChange={(duration) =>
                        updateSection(index, {
                          ...section,
                          onInterval: {
                            ...(section as IntervalSet).onInterval,
                            duration,
                            durationValid: duration > 0,
                          },
                        })
                      }
                      onValidityChange={(isValid) =>
                        updateSection(index, {
                          ...section,
                          onInterval: {
                            ...(section as IntervalSet).onInterval,
                            durationValid: isValid,
                          },
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium">Watts</label>
                    <input
                      type="number"
                      value={(
                        section as IntervalSet
                      ).onInterval.targetPower.toString()}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        updateSection(index, {
                          ...section,
                          onInterval: {
                            ...(section as IntervalSet).onInterval,
                            targetPower: Number(val),
                            powerValid: !!val,
                          },
                        });
                      }}
                      className={`border p-2 w-full ${(section as IntervalSet).onInterval.powerValid ? '' : 'border-red-500'}`}
                    />
                  </div>
                </div>

                {/* Rest Interval */}
                <div className="space-y-2">
                  <h4 className="font-medium text-blue-700">Rest Interval</h4>
                  <div>
                    <label className="block text-sm font-medium">Time</label>
                    <TimeInput
                      value={(section as IntervalSet).restInterval.duration}
                      onChange={(duration) =>
                        updateSection(index, {
                          ...section,
                          restInterval: {
                            ...(section as IntervalSet).restInterval,
                            duration,
                            durationValid: duration > 0,
                          },
                        })
                      }
                      onValidityChange={(isValid) =>
                        updateSection(index, {
                          ...section,
                          restInterval: {
                            ...(section as IntervalSet).restInterval,
                            durationValid: isValid,
                          },
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium">Watts</label>
                    <input
                      type="number"
                      value={(
                        section as IntervalSet
                      ).restInterval.targetPower.toString()}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        updateSection(index, {
                          ...section,
                          restInterval: {
                            ...(section as IntervalSet).restInterval,
                            targetPower: Number(val),
                            powerValid: !!val,
                          },
                        });
                      }}
                      className={`border p-2 w-full ${(section as IntervalSet).restInterval.powerValid ? '' : 'border-red-500'}`}
                    />
                  </div>
                </div>

                {/* Repeats */}
                <div className="space-y-2">
                  <h4 className="font-medium">Repeats</h4>
                  <div>
                    <label className="block text-sm font-medium">Count</label>
                    <input
                      type="number"
                      min="0"
                      value={(section as IntervalSet).repeats.toString()}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        if (inputValue === '') {
                          updateSection(index, {
                            ...section,
                            repeats: 0,
                            repeatsValid: false,
                          });
                        } else {
                          const val = parseInt(inputValue) || 0;
                          updateSection(index, {
                            ...section,
                            repeats: val,
                            repeatsValid: val > 0,
                          });
                        }
                      }}
                      className={`border p-2 w-full ${(section as IntervalSet).repeatsValid ? '' : 'border-red-500'}`}
                    />
                  </div>
                  <div className="text-sm text-gray-600 mt-4">
                    <p>Total section time:</p>
                    <p className="font-medium">
                      {formatTime(
                        ((section as IntervalSet).onInterval.duration +
                          (section as IntervalSet).restInterval.duration) *
                          (section as IntervalSet).repeats
                      )}
                    </p>
                  </div>
                </div>
              </div>
            ) : section.type === 'ramp' ? (
              // Ramp
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium">Time</label>
                  <TimeInput
                    value={(section as Ramp).duration}
                    onChange={(duration) =>
                      updateSection(index, { ...section, duration })
                    }
                    onValidityChange={(isValid) =>
                      updateSection(index, {
                        ...section,
                        durationValid: isValid,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">
                    Start Watts
                  </label>
                  <input
                    type="number"
                    value={(section as Ramp).startPower.toString()}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      if (inputValue === '') {
                        updateSection(index, {
                          ...section,
                          startPower: 0,
                          startPowerValid: false,
                        });
                      } else {
                        const val = parseInt(inputValue) || 0;
                        updateSection(index, {
                          ...section,
                          startPower: Number(val),
                          startPowerValid: val > 0,
                        });
                      }
                    }}
                    className={`border p-2 w-full ${(section as Ramp).startPowerValid ? '' : 'border-red-500'}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">End Watts</label>
                  <input
                    type="number"
                    value={(section as Ramp).endPower.toString()}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      if (inputValue === '') {
                        updateSection(index, {
                          ...section,
                          endPower: 0,
                          endPowerValid: false,
                        });
                      } else {
                        const val = parseInt(inputValue) || 0;
                        updateSection(index, {
                          ...section,
                          endPower: Number(val),
                          endPowerValid: val > 0,
                        });
                      }
                    }}
                    className={`border p-2 w-full ${(section as Ramp).endPowerValid ? '' : 'border-red-500'}`}
                  />
                </div>
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {/* Workout Summary */}
      {sections.length > 0 && (
        <div className="bg-blue-50 p-4 rounded">
          <h3 className="font-semibold mb-2">Workout Summary</h3>
          <p>
            Total workout time:{' '}
            <span className="font-medium">{formatTime(totalTime())}</span>
          </p>
          <p>Blocks: {sections.filter((s) => s.type === 'block').length}</p>
          <p>
            Interval sets:{' '}
            {sections.filter((s) => s.type === 'interval').length}
          </p>
          <p>Ramps: {sections.filter((s) => s.type === 'ramp').length}</p>
        </div>
      )}

      <button
        onClick={handleStart}
        disabled={!readyToStart}
        className={`p-3 rounded font-medium w-full ${readyToStart ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-gray-300 text-gray-700 cursor-not-allowed'}`}
      >
        {!isConnectedToTrainer
          ? 'Connect to Trainer First'
          : !isFormValid
            ? 'Complete All Fields'
            : 'Start Workout'}
      </button>
    </div>
  );
}
